import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n/i18n";
import { sharedMessages } from "../i18n/messages";

const focusable = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function LanguageSwitch({ compact = false }: { compact?: boolean }) {
  const { language, setLanguage, text } = useI18n();
  return (
    <div className={`site-language ${compact ? "compact" : ""}`} aria-label={text(sharedMessages.language)}>
      {(["en", "it"] as const).map((item) => (
        <button key={item} type="button" aria-pressed={language === item} onClick={() => setLanguage(item)}>
          {item.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function SiteNav() {
  const { text } = useI18n();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const menu = menuRef.current;
    const trigger = triggerRef.current;
    const background = [document.querySelector("main"), document.querySelector("footer")].filter(Boolean) as HTMLElement[];
    background.forEach((element) => element.setAttribute("inert", ""));
    const first = menu?.querySelector<HTMLElement>(focusable);
    first?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || !menu) return;
      const items = [...menu.querySelectorAll<HTMLElement>(focusable)];
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      background.forEach((element) => element.removeAttribute("inert"));
      window.setTimeout(() => trigger?.focus(), 0);
    };
  }, [open]);

  const menuItems = [
    [text(sharedMessages.home), "/"],
    [text(sharedMessages.websites), "/websites"],
    [text(sharedMessages.automations), "/automations"],
    [text(sharedMessages.contact), "/#contact"],
  ];

  return (
    <>
      <header className="site-nav">
        <a className="site-identity" href="/" aria-label="Mattia D’Elia home"><span>Mattia D’Elia</span></a>
        <div className="site-nav-space" aria-hidden="true" />
        <LanguageSwitch compact />
        <button
          className={`menu-trigger ${open ? "open" : ""}`}
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls="site-menu-overlay"
          aria-label={text(open ? sharedMessages.menuClose : sharedMessages.menuOpen)}
          onClick={() => setOpen((value) => !value)}
        >
          <span /><span /><span />
        </button>
      </header>
      <div
        className={`menu-overlay ${open ? "open" : ""}`}
        id="site-menu-overlay"
        ref={menuRef}
        aria-hidden={!open}
      >
        <nav aria-label={text(sharedMessages.menuOpen)}>
          {menuItems.map(([label, href]) => (
            <a key={href} href={href} tabIndex={open ? 0 : -1} onClick={() => setOpen(false)}>
              {label}
            </a>
          ))}
        </nav>
      </div>
    </>
  );
}
